import { isEqual } from "lodash";
import { PatchPayeeEntityInput, PayeeBaseDocumentInput } from "#graphql/supergraphTypes";
import { PayeeData } from "../../../../Context/PayeeContext";
import { PayeeBaseSupportingDocument } from "../../../../types";

/**
 * Transforms PayeeBaseSupportingDocument to PayeeBaseDocumentInput format
 */
const transformSupportingDocumentForMutation = (
  doc: PayeeBaseSupportingDocument,
): PayeeBaseDocumentInput => ({
  name: doc.name,
  size: doc.size,
  s3ObjectKey: doc.s3ObjectKey,
  ...(doc.contentType && { contentType: doc.contentType }),
});

/**
 * Compares two arrays of supporting documents for equality
 */
const areSupportingDocumentsEqual = (
  current?: PayeeBaseSupportingDocument[],
  original?: PayeeBaseSupportingDocument[],
): boolean => {
  if (!current && !original) return true;
  if (!current || !original) return false;
  if (current.length !== original.length) return false;

  // Transform both arrays to mutation format for comparison
  const currentTransformed = current.map(transformSupportingDocumentForMutation);
  const originalTransformed = original.map(transformSupportingDocumentForMutation);

  return isEqual(currentTransformed, originalTransformed);
};

/**
 * Generates a patch diff object for Entity payee mutations that only includes changed fields.
 * Compares the current payee state with the original payee data from context.
 *
 * @param currentPayeeData - The current payee data from context
 * @param originalPayeeData - The original payee data before any edits
 * @returns PatchPayeeEntityInput with only changed fields, or null if no changes
 * @throws Error if immutable fields (payeeId, arenaId) have changed
 */
export const generatePayeePatchDiff = (
  currentPayeeData: PayeeData,
  originalPayeeData: PayeeData,
): Omit<PatchPayeeEntityInput, "payeeId"> | null => {
  // Validate that immutable fields haven't changed
  if (currentPayeeData.payeeId !== originalPayeeData.payeeId) {
    throw new Error("PayeeId cannot be changed during edit operations");
  }

  if (currentPayeeData.arenaId !== originalPayeeData.arenaId) {
    throw new Error("ArenaId cannot be changed during edit operations");
  }

  const changes: Partial<Omit<PatchPayeeEntityInput, "payeeId">> = {};
  let hasChanges = false;

  // Compare payee name
  if (currentPayeeData.payeeName !== originalPayeeData.payeeName) {
    changes.name = currentPayeeData.payeeName;
    hasChanges = true;
  }

  // Compare payment preference ID
  const currentPaymentPrefId = currentPayeeData.paymentPref?.id;
  const originalPaymentPrefId = originalPayeeData.paymentPref?.id;

  if (currentPaymentPrefId !== originalPaymentPrefId) {
    changes.paymentPrefId = currentPaymentPrefId;
    hasChanges = true;
  }

  // Compare supporting documents
  if (
    !areSupportingDocumentsEqual(
      currentPayeeData.supportingDocuments,
      originalPayeeData.supportingDocuments,
    )
  ) {
    changes.supportingDocuments = currentPayeeData.supportingDocuments?.map(
      transformSupportingDocumentForMutation,
    );
    hasChanges = true;
  }

  return hasChanges ? changes : null;
};
